import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, ArrowUp, ArrowDown, Check } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { galleryUrl } from "./TeamGallery";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [captions, setCaptions] = useState({});

  const load = async () => {
    try { const { data } = await api.get("/gallery"); setItems(data); setCaptions(Object.fromEntries(data.map((i) => [i.id, i.caption || ""]))); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
      setUploading((n) => n - 1);
    }
    toast.success("Photos uploaded");
    load();
  };

  const saveCaption = async (id) => {
    try { await api.patch(`/admin/gallery/${id}`, { caption: captions[id] }); toast.success("Caption saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    try {
      await Promise.all([api.patch(`/admin/gallery/${items[i].id}`, { order: j }), api.patch(`/admin/gallery/${items[j].id}`, { order: i })]);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <label className="flex items-center justify-center gap-3 cursor-pointer rounded-2xl border-2 border-dashed border-slate-700 bg-slate-900/40 px-6 py-10 text-slate-300 hover:border-amber-500/60 transition-colors">
        {uploading ? <Loader2 className="w-5 h-5 animate-spin text-amber-400" /> : <Upload className="w-5 h-5 text-amber-400" />}
        <span className="text-sm">{uploading ? `Uploading ${uploading} photo${uploading > 1 ? "s" : ""}…` : "Click to upload team & fleet photos (JPG/PNG/WEBP, up to 5MB each, multiple allowed)"}</span>
        <input data-testid="gallery-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={!!uploading} />
      </label>
      <div className="mt-3 text-xs text-slate-500">Photos appear on the About page in this order. First photo is featured larger.</div>

      {items.length === 0 ? (
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-500 text-sm">No photos yet — upload your first driver or truck photo above.</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.id} data-testid={`admin-gallery-item-${it.id}`} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
              <img src={galleryUrl(it.file_id)} alt={it.caption || ""} className="w-full aspect-[4/3] object-cover" />
              <div className="p-3 space-y-2">
                <div className="flex gap-2">
                  <Input data-testid={`admin-gallery-caption-${it.id}`} value={captions[it.id] ?? ""} onChange={(e) => setCaptions((c) => ({ ...c, [it.id]: e.target.value }))} placeholder="Caption (optional)" className="bg-slate-950 border-slate-700 text-white text-sm" />
                  <Button data-testid={`admin-gallery-save-${it.id}`} size="icon" variant="outline" className="border-slate-700 text-slate-200 shrink-0" onClick={() => saveCaption(it.id)} title="Save caption"><Check className="w-4 h-4" /></Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <Button data-testid={`admin-gallery-up-${it.id}`} size="icon" variant="ghost" className="text-slate-400 hover:text-white" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                    <Button data-testid={`admin-gallery-down-${it.id}`} size="icon" variant="ghost" className="text-slate-400 hover:text-white" disabled={i === items.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                  </div>
                  <Button data-testid={`admin-gallery-delete-${it.id}`} size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => remove(it.id)}><Trash2 className="w-4 h-4 mr-1" /> Remove</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
